import Decimal from 'decimal.js';

Decimal.set({ precision: 10, rounding: Decimal.ROUND_HALF_UP });

export type CurrencyCode = 'KRW' | 'USD';

export function roundByCurrency(
  amount: number | string | Decimal,
  currency: CurrencyCode,
): number {
  const decimal = new Decimal(amount);

  if (currency === 'KRW') {
    return decimal.toDecimalPlaces(0).toNumber();
  }

  return decimal.toDecimalPlaces(2).toNumber();
}

export function multiplyPrice(
  price: number,
  quantity: number,
  currency: CurrencyCode,
): number {
  const result = new Decimal(price).times(quantity);
  return roundByCurrency(result, currency);
}

export function sumPrices(prices: number[], currency: CurrencyCode): number {
  const result = prices.reduce((sum, price) => sum.plus(price), new Decimal(0));
  return roundByCurrency(result, currency);
}

export function addPrices(
  price1: number,
  price2: number,
  currency: CurrencyCode,
): number {
  const result = new Decimal(price1).plus(price2);
  return roundByCurrency(result, currency);
}

export function subtractPrices(
  price1: number,
  price2: number,
  currency: CurrencyCode,
): number {
  const result = new Decimal(price1).minus(price2);
  return roundByCurrency(result, currency);
}

export function dividePrice(
  price: number,
  divisor: number,
  currency: CurrencyCode,
): number {
  const result = new Decimal(price).dividedBy(divisor);
  return roundByCurrency(result, currency);
}

export function convertKRWtoUSD(krwAmount: number): number {
  const EXCHANGE_RATE = 1350;
  const result = new Decimal(krwAmount).dividedBy(EXCHANGE_RATE);
  return roundByCurrency(result, 'USD');
}

export function calculateMarginPercent(
  profit: number,
  totalPrice: number,
): number {
  if (totalPrice === 0) return 0;
  const result = new Decimal(profit).dividedBy(totalPrice).times(100);
  return result.toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
}

export function validateKRW(amount: number): boolean {
  return Number.isInteger(amount) || amount % 1 === 0;
}

export function formatPrice(price: number, currency: CurrencyCode): string {
  if (currency === 'USD') {
    return price.toFixed(2);
  }
  return Math.floor(price).toLocaleString();
}

export function getCurrencySymbol(currency: CurrencyCode): string {
  return currency === 'USD' ? '$' : '₩';
}

export function sanitizePriceInput(
  value: string,
  currency: CurrencyCode,
): string {
  let sanitized = value.replace(/[^\d.]/g, '');

  const parts = sanitized.split('.');
  if (parts.length > 2) {
    sanitized = `${parts[0]}.${parts.slice(1).join('')}`;
  }

  if (currency === 'KRW') {
    sanitized = sanitized.replace(/\./g, '');
  } else if (currency === 'USD') {
    const [integer, decimal] = sanitized.split('.');
    if (decimal !== undefined) {
      sanitized = `${integer}.${decimal.slice(0, 2)}`;
    }
  }

  return sanitized;
}

export function parsePrice(value: string): number {
  const cleaned = value.replace(/[^\d.]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}
